import React from 'react';
import { Container, Typography, Grid, Card, CardContent, Stack, Chip, Box, Paper, Divider, LinearProgress } from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Settings as SettingsIcon,
  Mic as MicIcon,
  Link as LinkIcon,
  SwapHoriz as SwapHorizIcon,
  NetworkCheck as NetworkCheckIcon,
  VolumeUp as VolumeUpIcon,
  ChatBubble as ChatBubbleIcon,
  Stop as StopIcon,
  CloudUpload as CloudUploadIcon,
  Security as SecurityIcon,
  Code as CodeIcon
} from '@mui/icons-material';

const flowSteps = [
  {
    phase: 1,
    title: "Initialization & Session Setup",
    color: "primary",
    steps: [
      {
        icon: <PlayArrowIcon fontSize="small" color="primary" />,
        title: "Client: Request Voice Session",
        description: "POST /conversations/{conversation_id}/voice/start with Authorization token",
        details: [
          "Frontend calls backend API endpoint",
          "Backend validates token and conversation_id",
          "Backend generates unique voiceSessionId (UUID)",
          "Backend returns session info including ICE servers and WebSocket URL"
        ],
        code: `fetch('/conversations/{conversation_id}/voice/start', {
  method: 'POST',
  headers: {
    'Authorization': authToken,
    'Content-Type': 'application/json'
  }
})`
      },
      {
        icon: <SettingsIcon fontSize="small" color="primary" />,
        title: "Client: Initialize RTCPeerConnection",
        description: "Create WebRTC peer connection with ICE servers configuration",
        details: [
          "Initialize RTCPeerConnection with received ICE servers",
          "Configure connection handlers (ontrack, onicecandidate)",
          "Set up remote audio track handler"
        ],
        code: `const pc = new RTCPeerConnection({ 
  iceServers: [{ urls: ["stun:stun.l.google.com:19302"] }]
})`
      }
    ]
  },
  {
    phase: 2,
    title: "Media Acquisition & Authentication",
    color: "secondary",
    steps: [
      {
        icon: <MicIcon fontSize="small" color="primary" />,
        title: "Client: Get User Media",
        description: "Request microphone access and add audio track to peer connection",
        details: [
          "Request getUserMedia({ audio: true })",
          "Get user's microphone stream",
          "Add audio track to RTCPeerConnection",
          "Track will be transmitted to server once connection is established"
        ],
        code: `const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
stream.getTracks().forEach(track => pc.addTrack(track, stream));`
      },
      {
        icon: <LinkIcon fontSize="small" color="primary" />,
        title: "Client: Connect Signaling WebSocket",
        description: "Establish WebSocket connection to signaling endpoint",
        details: [
          "Connect to wss://{host}/signal/{voice_session_id}",
          "Wait for WebSocket open event",
          "Prepare for bidirectional signaling communication"
        ],
        code: `const ws = new WebSocket(assistantNodeUrl);
ws.onopen = () => { /* ready for signaling */ };`
      },
      {
        icon: <SecurityIcon fontSize="small" color="primary" />,
        title: "Client: Send Authentication",
        description: "Authenticate WebSocket connection with session ID and token",
        details: [
          "Send first message with type: 'auth'",
          "Include voiceSessionId and token in message",
          "Server validates and establishes authenticated session"
        ],
        code: `ws.send(JSON.stringify({
  type: "auth",
  token: authToken,
  voiceSessionId
}));`
      }
    ]
  },
  {
    phase: 3,
    title: "WebRTC Negotiation",
    color: "primary",
    steps: [
      {
        icon: <SwapHorizIcon fontSize="small" color="primary" />,
        title: "Client: Create & Send Offer",
        description: "Create WebRTC offer and send to server via WebSocket",
        details: [
          "Call pc.createOffer() to generate SDP offer",
          "Set local description with offer",
          "Send offer SDP to server via WebSocket",
          "Offer includes client's media capabilities"
        ],
        code: `const offer = await pc.createOffer();
await pc.setLocalDescription(offer);
ws.send(JSON.stringify({
  type: "offer",
  voiceSessionId,
  sdp: pc.localDescription.sdp
}));`
      },
      {
        icon: <CloudUploadIcon fontSize="small" color="primary" />,
        title: "Server: Process Offer & Create Answer",
        description: "Server receives offer, creates peer connection, and generates answer",
        details: [
          "Server receives offer via WebSocket",
          "Server creates RTCPeerConnection with ICE servers",
          "Server sets remote description (offer)",
          "Server creates answer SDP",
          "Server sets local description (answer)",
          "Server sends answer back to client"
        ],
        code: `offer = RTCSessionDescription(sdp=data["sdp"], type="offer")
await pc.setRemoteDescription(offer)
answer = await pc.createAnswer()
await pc.setLocalDescription(answer)
await ws.send_json({
  "type": "answer",
  "sdp": pc.localDescription.sdp
})`
      },
      {
        icon: <NetworkCheckIcon fontSize="small" color="primary" />,
        title: "Bidirectional: ICE Candidate Exchange",
        description: "Both client and server exchange ICE candidates for connection",
        details: [
          "Client generates ICE candidates",
          "Client sends candidates to server via WebSocket",
          "Server generates ICE candidates",
          "Server sends candidates to client via WebSocket",
          "Both sides add received candidates to peer connection",
          "ICE negotiation establishes optimal connection path"
        ],
        code: `// Client side
pc.onicecandidate = (event) => {
  if (event.candidate) {
    ws.send(JSON.stringify({
      type: "iceCandidate",
      candidate: event.candidate
    }));
  }
};

// Server side
@pc.on("icecandidate")
async def on_icecandidate(event):
  candidate = event.candidate
  if candidate:
    await ws.send_json({
      "type": "iceCandidate",
      "candidate": candidate
    })`
      }
    ]
  },
  {
    phase: 4,
    title: "Audio Communication",
    color: "success",
    steps: [
      {
        icon: <VolumeUpIcon fontSize="small" color="primary" />,
        title: "Client: Handle Remote Audio",
        description: "Receive and play assistant's audio response",
        details: [
          "Client listens for 'track' events on peer connection",
          "Extract remote audio stream from event",
          "Create audio element and set srcObject",
          "Auto-play assistant's voice response"
        ],
        code: `pc.ontrack = (event) => {
  const [remoteStream] = event.streams;
  const audioElem = document.createElement('audio');
  audioElem.srcObject = remoteStream;
  audioElem.autoplay = true;
};`
      },
      {
        icon: <ChatBubbleIcon fontSize="small" color="primary" />,
        title: "Server: Process Audio & Generate Response",
        description: "Server receives audio, processes through STT->LLM->TTS pipeline",
        details: [
          "Server receives audio track from client",
          "Process audio frames through Speech-to-Text (STT)",
          "Send transcribed text to LLM for processing",
          "Convert LLM response to speech via Text-to-Speech (TTS)",
          "Send audio track back to client via peer connection",
          "Note: Currently implementation shows structure, full pipeline to be integrated"
        ],
        code: `@pc.on("track")
async def on_track(track):
  if track.kind == "audio":
    while True:
      frame = await track.recv()
      # STT -> LLM -> TTS pipeline
      # Process and send response back`
      }
    ]
  },
  {
    phase: 5,
    title: "Session Management",
    color: "info",
    steps: [
      {
        icon: <StopIcon fontSize="small" color="primary" />,
        title: "Termination: Hangup & Cleanup",
        description: "End voice session and clean up resources",
        details: [
          "Client sends 'hangup' message via WebSocket",
          "Server closes peer connection",
          "Server removes session from PEER_CONNECTIONS dict",
          "Client closes WebSocket connection",
          "Client closes peer connection",
          "Client stops all media tracks",
          "All resources properly released"
        ],
        code: `// Client
ws.send(JSON.stringify({ type: "hangup", voiceSessionId }));
ws.close();
pc.close();

// Server
if t == "hangup":
  await pc.close()
  del PEER_CONNECTIONS[voice_session_id]`
      }
    ]
  }
];

function FlowStepCard({ icon, title, description, details, code }) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: 6,
          borderColor: 'primary.light',
        },
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
        <Divider />
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
            Details:
          </Typography>
          <Stack spacing={0.5} sx={{ pl: 1 }}>
            {details.map((detail, idx) => (
              <Typography key={idx} variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                • {detail}
              </Typography>
            ))}
          </Stack>
        </Box>
        {code && (
          <>
            <Divider />
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <CodeIcon fontSize="small" color="primary" />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Code Example:
                </Typography>
              </Stack>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  backgroundColor: 'grey.50',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <Typography
                  component="pre"
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: 0,
                  }}
                >
                  {code}
                </Typography>
              </Paper>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PhaseSection({ phase, title, color, steps }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 4,
        borderLeft: (theme) => `6px solid ${theme.palette[color].main}`,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Chip label={`Phase ${phase}`} color={color} sx={{ fontWeight: 700 }} />
        <Typography variant="h6">{title}</Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr' },
          alignItems: 'stretch',
        }}
      >
        {steps.map((step, idx) => (
          <FlowStepCard key={idx} {...step} />
        ))}
      </Box>
    </Paper>
  );
}

function ConnectionFlowDiagram() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        mb: 4,
        p: 3,
        backgroundColor: 'background.default',
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>Connection Flow</Typography>
      <Box sx={{ width: '100%', maxWidth: 800 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Paper sx={{ p: 2, flex: 1, textAlign: 'center', backgroundColor: 'primary.light', color: 'white' }}>
              <Typography variant="subtitle2" fontWeight={600}>Client (Browser)</Typography>
            </Paper>
            <Arrow />
            <Paper sx={{ p: 2, flex: 1, textAlign: 'center', backgroundColor: 'secondary.light', color: 'white' }}>
              <Typography variant="subtitle2" fontWeight={600}>Server (Backend)</Typography>
            </Paper>
          </Box>
          <LinearProgress variant="determinate" value={0} sx={{ height: 4, borderRadius: 2 }} />
          <Typography variant="caption" color="text.secondary" textAlign="center">
            WebSocket Signaling Channel (wss://)
          </Typography>
          <LinearProgress variant="determinate" value={0} sx={{ height: 4, borderRadius: 2 }} />
          <Typography variant="caption" color="text.secondary" textAlign="center">
            WebRTC Peer Connection (UDP/TCP via ICE)
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

function Arrow() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <SwapHorizIcon color="primary" />
    </Box>
  );
}

export default function VoiceChatFlow() {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={2} sx={{ mb: 4 }}>
        <Typography variant="h4" textAlign="center">
          Voice Chat Flow Diagram
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Detailed workflow of WebRTC-based live voice chatting for AI chat assistant
        </Typography>
      </Stack>

      <ConnectionFlowDiagram />

      {flowSteps.map((phase) => (
        <PhaseSection key={phase.phase} {...phase} />
      ))}

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 4,
          backgroundColor: 'info.light',
          color: 'info.contrastText',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>Key Technologies</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>WebRTC (Web Real-Time Communication)</Typography>
            <Typography variant="body2">
              Enables peer-to-peer audio communication directly between browser and server without requiring plugins.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>WebSocket Signaling</Typography>
            <Typography variant="body2">
              Bi-directional communication channel for exchanging SDP offers/answers and ICE candidates during connection establishment.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>ICE (Interactive Connectivity Establishment)</Typography>
            <Typography variant="body2">
              Protocol for finding the best network path between client and server, handling NAT traversal using STUN/TURN servers.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>STT → LLM → TTS Pipeline</Typography>
            <Typography variant="body2">
              Speech-to-Text converts audio to text, Large Language Model processes the conversation, and Text-to-Speech generates voice response.
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}

